import type { DeliveryZone, RsuiteSortType } from '@@partner:types:app'

import React, { useCallback } from 'react'

import { Link, generatePath } from 'react-router-dom'
import { Button, Tag } from 'rsuite'
import CommonStatusLabel from '@components/common/CommonStatusLabel'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'

import { ROUTE_DELIVERY_ZONES_EDIT } from '@@partner:config/routes'
import { DELIVERY_CLAIM_TYPE_LABELS } from '@@partner:helpers/deliveryClaim'
import { formatPrice } from '@@partner:helpers/util'
import { PageInfo } from '@@partner:types:graphql'

export interface DeliveryZoneTableProps {
  brandSlug: string
  deliveryZones: DeliveryZone[]
  pageInfo?: PageInfo
  loading?: boolean
  sortColumn?: string
  sortType?: RsuiteSortType
  onSortColumn?: (sortColumn: string, sortType?: RsuiteSortType) => void
}

const columns: ResponsiveTableColumn<DeliveryZone>[] = [
  {
    dataKey: 'id',
    header: 'ID',
    sortable: true,
    flexGrow: 1,
    hideByDefault: true
  },
  {
    dataKey: 'name',
    header: 'Название',
    sortable: true,
    flexGrow: 2,
    isCardTitle: true
  },
  {
    dataKey: 'shop.name',
    header: 'Название магазина',
    flexGrow: 2,
    render: (zone) => zone.shop?.name ?? '-'
  },
  {
    dataKey: 'shop.address',
    header: 'Адрес магазина',
    flexGrow: 2,
    hideOnMobile: true,
    hideByDefault: true,
    render: (zone) => zone.shop?.address ?? '-'
  },
  {
    dataKey: 'status',
    header: 'Статус',
    sortable: true,
    flexGrow: 1,
    isCardBadge: true,
    render: (zone) => <CommonStatusLabel status={zone.status} />
  },
  {
    dataKey: 'deliveryProviders',
    header: 'Провайдеры доставки',
    flexGrow: 2,
    render: (zone) => (
      <>
        {zone.deliveryProviders?.map(p => (
          <Tag key={p}>{DELIVERY_CLAIM_TYPE_LABELS.find(t => t.type === p)?.label ?? p}</Tag>
        ))}
      </>
    )
  },
  {
    dataKey: 'minOrderCost',
    header: 'Минимальная корзина',
    flexGrow: 1,
    render: (zone) => (
      <div>{ formatPrice(zone.minOrderCost) }</div>
    )
  },
  {
    dataKey: 'customer_order',
    header: 'Порядок',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => zone.customer_order ?? '-'
  },
  {
    dataKey: 'zoneSlotMaxWeight',
    header: 'Макс. вес слот',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => zone.zoneSlotMaxWeight ? `${zone.zoneSlotMaxWeight} кг` : '-'
  },
  {
    dataKey: 'zoneDeliveryCost',
    header: 'Стоимость доставки слот',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => formatPrice(zone.zoneDeliveryCost)
  },
  {
    dataKey: 'zoneFirstDeliveryCost',
    header: 'Стоимость первой доставки',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => formatPrice(zone.zoneFirstDeliveryCost)
  },
  {
    dataKey: 'freeSlotDeliveryCostFrom',
    header: 'Бесплатная доставка слот от',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => formatPrice(zone.freeSlotDeliveryCostFrom)
  },
  {
    dataKey: 'zoneExpressMaxWeight',
    header: 'Макс. вес экспресс',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => zone.zoneExpressMaxWeight ? `${zone.zoneExpressMaxWeight} кг` : '-'
  },
  {
    dataKey: 'zoneExpressDeliveryCost',
    header: 'Стоимость экспресс',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => formatPrice(zone.zoneExpressDeliveryCost)
  },
  {
    dataKey: 'freeExpressDeliveryCostFrom',
    header: 'Бесплатный экспресс от',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => formatPrice(zone.freeExpressDeliveryCostFrom)
  },
  {
    dataKey: 'expressDeliveryMaxShippingTime',
    header: 'Макс. время экспресс (мин)',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => zone.expressDeliveryMaxShippingTime ? `${zone.expressDeliveryMaxShippingTime} мин` : '-'
  },
  {
    dataKey: 'tzOffset',
    header: 'Часовой пояс',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => zone.tzOffset ? `UTC ${zone.tzOffset > 0 ? '+' : ''}${zone.tzOffset / 3600}` : '-'
  },
  {
    dataKey: 'is_serving_retail',
    header: 'Розница',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => zone.is_serving_retail ? 'Да' : 'Нет'
  },
  {
    dataKey: 'is_serving_wholesale',
    header: 'Опт',
    flexGrow: 1,
    hideByDefault: true,
    render: (zone) => zone.is_serving_wholesale ? 'Да' : 'Нет'
  }
]

export default function DeliveryZoneTable ({
  brandSlug,
  deliveryZones = [],
  pageInfo,
  loading = false,
  sortColumn,
  sortType,
  onSortColumn
}: DeliveryZoneTableProps) {
  const renderActions = useCallback((zone: DeliveryZone) => (
    <Button
      color="cyan"
      appearance="primary"
      as={Link}
      to={generatePath(ROUTE_DELIVERY_ZONES_EDIT, {
        brandSlug,
        zoneId: zone.id
      })}
      size="xs"
    >
      Редактировать
    </Button>
  ), [ brandSlug ])

  return (
    <ResponsiveTable
      data={deliveryZones}
      columns={columns}
      loading={loading}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={onSortColumn}
      renderActions={renderActions}
      storageKey="delivery-zones"
      enableColumnToggle={true}
      total={pageInfo?.total}
      totalLabel='Найдено зон'
    />
  )
}
